// hooks/useFilterUrlSync.ts
import { useEffect, useRef, useMemo, useState, useCallback } from "react";
import { usePathname } from "next/navigation";
import {
  SelectedFilters,
  FilterRequest,
} from "@/app/lib/features/filters/filters.types";
import { defaultSelected } from "@/app/lib/features/filters/filters.state";
import { setUrlOwnedKeys } from "@/app/lib/features/filters/filters.slice";
import { useAppDispatch } from "@/app/lib/features/redux/redux.hook";
import {
  buildFilterRequestFromSelected,
  buildQueryString,
  parseFiltersFromSearchParams,
} from "../utils/filters.utils";

interface UseFilterUrlSyncOptions {
  selected: SelectedFilters;
  updateSelected: (filters: Partial<SelectedFilters>) => void;
  baseFilterRequest?: Partial<FilterRequest>;
  preserveParams?: string[];
  /** Sayfa bağlamından gelen preselected filtreler (slugControl item_ids vb.).
   *  URL params varsa onlar öncelikli, yoksa bu değer mount'ta Redux'a yüklenir. */
  initialSelected?: Partial<SelectedFilters>;
  /** Kullanıcı henüz hiçbir filtreye dokunmadıysa (0) URL'e filtre yazma —
   *  bağlam/serverSelected değerlerinin temiz URL'e sızmasını engeller.
   *  İlk kullanıcı etkileşiminden sonra (>0) normal URL sync devreye girer. */
  interactionCount?: number;
  /** Her zaman URL'den dışlanacak key'ler — bağlam filtreleri (category, category_groups vb.)
   *  kullanıcı değiştirse bile URL'e yazılmaz; bunlar slug sayfası bağlamından gelir. */
  excludeFromUrl?: (keyof SelectedFilters)[];
}

/** Sıra-bağımsız eşitlik — array'ler string'e normalize edilip sort'lanır. */
const valuesEqual = (a: unknown, b: unknown): boolean => {
  const norm = (v: unknown) =>
    Array.isArray(v)
      ? JSON.stringify([...v].map(String).sort())
      : JSON.stringify(v);
  return norm(a) === norm(b);
};

/** selected'ı sıra-bağımsız, alan-sıralı bir imzaya çevirir.
 *  Init'in yazdığı state ile kullanıcı değişikliğini ayırt etmek için kullanılır. */
const selectedSignature = (sel: SelectedFilters): string => {
  const keys = Object.keys(sel).sort() as (keyof SelectedFilters)[];
  return keys
    .map((k) => {
      const v = sel[k];
      const norm = Array.isArray(v)
        ? JSON.stringify([...v].map(String).sort())
        : JSON.stringify(v);
      return `${k}:${norm}`;
    })
    .join("|");
};

const useFilterUrlSync = ({
  selected,
  updateSelected,
  baseFilterRequest = {},
  preserveParams = [],
  initialSelected,
  interactionCount = 0,
  excludeFromUrl = [],
}: UseFilterUrlSyncOptions) => {
  const pathname = usePathname();
  const dispatch = useAppDispatch();
  const mountedRef = useRef(true);
  const isInternalUpdateRef = useRef(false);
  const preserveParamsRef = useRef(preserveParams);

  // Her slug/path için init tamamlandı mı — fetch effect'i buna bakarak "boş key" fetch'ini engeller.
  const [initReady, setInitReady] = useState(false);

  // Bağlam filtrelerinin (initialSelected) referansını ref'te tut —
  // URL sync effect'inde "kullanıcı üzerine ekledi mi" karşılaştırması için.
  const initialSelectedRef = useRef(initialSelected);

  // Init'in yazdığı selected'ın imzası. URL sync bu imzayla eşleşen state'i
  // (yani init'in ürettiği state'i) URL'e yazmaz — sadece kullanıcı değişikliğini yazar.
  const initSignatureRef = useRef<string>("");

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const selectedKey = JSON.stringify(selected);
  const baseFilterRequestKey = JSON.stringify(baseFilterRequest);

  // mountPartial: mount anında URL'den parse edilen filtreler.
  // FavoritesTab gibi tüketiciler ilk render'da Redux sync beklemeden fallback için kullanır.
  //
  // useSearchParams() BİLEREK kullanılmıyor: statik prerender edilen (ISR) bir
  // route'ta useSearchParams çağıran client component, en yakın Suspense sınırını
  // komple CSR'a düşürüyor (BAILOUT_TO_CLIENT_SIDE_RENDERING) — bu hook tüm
  // listeleme sayfalarında kullanıldığı için ürünler ISR HTML'ine hiç girmiyordu.
  // window.location sunucuda okunmaz; SSR'da boş, client'ta gerçek değer döner.
  const [mountPartial] = useState<Partial<SelectedFilters>>(() =>
    typeof window === "undefined"
      ? {}
      : parseFiltersFromSearchParams(
          new URLSearchParams(window.location.search),
        ),
  );

  // activeFilterRequest: TEK KAYNAK selected. Override hilesi yok.
  const activeFilterRequest = useMemo(
    () => buildFilterRequestFromSelected(selected, baseFilterRequest),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedKey, baseFilterRequestKey],
  );

  // Init: pathname veya initialSelected değişince çalışır.
  // initialSelected slugData'dan gelir — ilk render'da boş olabilir,
  // API dönünce dolar. Guard: pathname+initialSelectedKey çifti izlenir.
  const initializedPathRef = useRef<string | null>(null);
  const initializedInitKeyRef = useRef<string | null>(null);

  // Stable key — undefined ve {} aynı sayılır (her ikisi de "bağlam yok")
  const initialSelectedKey = JSON.stringify(
    initialSelected && Object.keys(initialSelected).length > 0
      ? initialSelected
      : null,
  );

  /** URL'den parse edip Redux'a yaz. popstate ve normal init tarafından paylaşılır. */
  const applyUrlToState = useCallback(
    (currentPathname: string, currentInitialSelected?: Partial<SelectedFilters>) => {
      const urlPartial = parseFiltersFromSearchParams(
        new URLSearchParams(window.location.search),
      );

      const fullSelected: SelectedFilters = {
        ...defaultSelected,
        ...(currentInitialSelected ?? {}),
        ...urlPartial,
      } as SelectedFilters;

      initSignatureRef.current = selectedSignature(fullSelected);
      initialSelectedRef.current = currentInitialSelected;
      initializedPathRef.current = currentPathname;

      isInternalUpdateRef.current = true;
      // URL'de açıkça yazan key'ler işaretlenir: getFilters yanıtındaki bağlam
      // merge'ü (userInteractionCount === 0 iken çalışır) bunları ezmesin.
      // Remount'ta sayaç sıfırlandığı için ?gender=7 bağlam gender'ıyla siliniyordu.
      dispatch(
        setUrlOwnedKeys(Object.keys(urlPartial) as (keyof SelectedFilters)[]),
      );
      updateSelected(fullSelected);
      setInitReady(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateSelected, dispatch],
  );

  useEffect(() => {
    if (
      initializedPathRef.current === pathname &&
      initializedInitKeyRef.current === initialSelectedKey
    ) {
      return;
    }

    const isNewPath = initializedPathRef.current !== pathname;

    // Pathname değiştiyse initReady'i sıfırla
    if (isNewPath) {
      setInitReady(false);
    }

    initializedInitKeyRef.current = initialSelectedKey;

    applyUrlToState(pathname, initialSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, initialSelectedKey]);

  // Tarayıcı geri/ileri navigasyonu (popstate): aynı pathname'de URL değişince
  // init effect tetiklenmez — bu effect Redux'u URL ile senkronize eder.
  useEffect(() => {
    const handlePopState = () => {
      applyUrlToState(pathname, initialSelectedRef.current);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, applyUrlToState]);

  // State → URL sync (internal update'leri atla).
  // Init'in ürettiği state URL'e yazılmaz (initSignatureRef eşleşmesi).
  // Bağlam filtreleri kullanıcı değiştirmediyse URL'e yazılmaz (sıra-bağımsız).
  useEffect(() => {
    if (!initReady) return;
    if (!mountedRef.current) return;
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      return;
    }

    // Kullanıcı henüz hiçbir filtreye dokunmadı — bağlam/serverSelected değerleri
    // (backend'in döndürdüğü preselected kategoriler) temiz URL'e YAZILMAMALI.
    if (interactionCount === 0) {
      return;
    }

    const ctxKeys = initialSelectedRef.current
      ? (Object.keys(initialSelectedRef.current) as (keyof SelectedFilters)[])
      : [];
    const unchangedInitialKeys = [
      // initialSelected'dan gelen değişmemiş key'ler
      ...ctxKeys.filter((k) =>
        valuesEqual(selected[k], initialSelectedRef.current![k]),
      ),
      // Her zaman URL'den dışlanan bağlam key'leri (category_groups vb.)
      ...excludeFromUrl,
    ];

    const filterQuery = buildQueryString(selected, unchangedInitialKeys);
    const preserved = new URLSearchParams();

    // Effect client-only → window.location güvenli (useSearchParams CSR bailout'a
    // yol açtığı için kullanılmıyor, bkz. mountPartial notu).
    const currentParams = new URLSearchParams(window.location.search);
    preserveParamsRef.current.forEach((key) => {
      const val = currentParams.get(key);
      if (val) preserved.set(key, val);
    });

    const preservedQuery = preserved.toString();
    const combined = [preservedQuery, filterQuery].filter(Boolean).join("&");
    const newUrl = `${pathname}${combined ? `?${combined}` : ""}`;

    window.history.replaceState(null, "", newUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, pathname, initReady, interactionCount]);

  return { activeFilterRequest, initReady, mountPartial };
};

export default useFilterUrlSync;